import { useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://usfansspreadsheet.icu";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => {
    return articles.find((a) => a.slug === slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const canonicalUrl = article ? `${SITE_URL}/artykul/${article.slug}` : `${SITE_URL}/`;
  const pageTitle = article ? `${article.title} | USFansSpreadsheet Polska` : "Strona nie znaleziona – USFansSpreadsheet Polska";
  const pageDescription = article
    ? article.description
    : "Przepraszamy, artykuł którego szukasz nie istnieje. Wróć na stronę główną USFansSpreadsheet Polska.";
  const pageKeywords = article
    ? `usfansspreadsheet, ${article.title}, ranking, porównanie, polska, przewodnik, opinie`
    : "usfansspreadsheet, strona główna, przewodnik";

  const articleSchema = article
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        description: article.description,
        url: `${SITE_URL}/artykul/${article.slug}`,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${SITE_URL}/artykul/${article.slug}`,
        },
        datePublished: article.date,
        dateModified: "2026-06-24",
        author: { "@type": "Person", name: article.author, url: `${SITE_URL}/` },
        publisher: {
          "@type": "Organization",
          name: "USFansSpreadsheet Polska",
          url: `${SITE_URL}/`,
        },
        inLanguage: "pl",
        wordCount: article.wordCount,
        image: article.image,
      }
    : null;

  const breadcrumbSchema = article
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Strona Główna",
            item: `${SITE_URL}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: article.title,
            item: `${SITE_URL}/artykul/${article.slug}`,
          },
        ],
      }
    : null;

  const faqSchema = useMemo(() => {
    if (!article || article.slug !== "faq-pytania") return null;
    const mainEntity: Array<{"@type": "Question"; name: string; acceptedAnswer: {"@type": "Answer"; text: string}}> = [];
    for (let i = 0; i < article.sections.length; i++) {
      const section = article.sections[i];
      if (section.type === "h2" && i > 0) {
        const nextSection = article.sections[i + 1];
        if (nextSection && nextSection.type === "p") {
          const questionText = section.content.replace(/<[^>]+>/g, "");
          const answerText = nextSection.content.replace(/<[^>]+>/g, "");
          mainEntity.push({
            "@type": "Question",
            name: questionText,
            acceptedAnswer: {
              "@type": "Answer",
              text: answerText,
            },
          });
        }
      }
    }
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity,
    };
  }, [article]);

  if (!article) {
    return (
      <>
        <meta name="robots" content="noindex, nofollow" />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <div className="min-h-screen flex flex-col">
          <Header />
          <main className="flex-1 flex items-center justify-center pt-20">
            <div className="text-center px-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary-100 flex items-center justify-center">
                <i className="ri-error-warning-line text-secondary-600 text-2xl"></i>
              </div>
              <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">Artykuł nie znaleziony</h1>
              <p className="text-foreground-600 mb-6">Przepraszamy, artykuł którego szukasz nie istnieje.</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-900 text-background-50 text-sm font-semibold rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-4-line text-base"></i>
                Wróć do strony głównej
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={article.title} />
      <meta property="og:description" content={article.description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:locale" content="pl_PL" />
      <meta property="og:image" content={article.image} />
      <meta property="article:published_time" content={article.date} />
      <meta property="article:modified_time" content="2026-06-24" />
      <meta property="article:author" content={article.author} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={article.title} />
      <meta name="twitter:description" content={article.description} />
      <meta name="twitter:image" content={article.image} />

      {articleSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(articleSchema),
          }}
        />
      )}
      {breadcrumbSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbSchema),
          }}
        />
      )}
      {faqSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqSchema),
          }}
        />
      )}

      <div className="min-h-screen flex flex-col">
        <Header />

        <main>
          <section className="relative w-full pt-28 md:pt-32 pb-10 md:pb-14 bg-background-50">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-8">
                <span className="inline-block px-4 py-1.5 bg-secondary-100 text-secondary-800 text-xs font-medium rounded-full mb-5">
                  Artykuł SEO
                </span>
                <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
                  {article.title}
                </h1>
                <p className="text-sm md:text-base text-foreground-600 mb-6 max-w-2xl mx-auto leading-relaxed">
                  {article.description}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-900 text-background-50 text-sm font-semibold rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line text-base"></i>
                    Wróć do strony głównej
                  </Link>
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-transparent border-2 border-foreground-900 text-foreground-900 text-sm font-semibold rounded-full hover:bg-foreground-900 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Kup teraz
                    <i className="ri-shopping-bag-line text-base"></i>
                  </a>
                </div>
                <div className="flex items-center justify-center gap-4 text-xs text-foreground-500 flex-wrap">
                  <span>Autor: {article.author}</span>
                  <span className="hidden sm:inline">&middot;</span>
                  <span>{article.date}</span>
                  <span className="hidden sm:inline">&middot;</span>
                  <span>Czas czytania: {article.readTime}</span>
                </div>
              </div>

              <div className="relative w-full aspect-[21/9] rounded-xl overflow-hidden mb-10">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </section>

          <article className="relative w-full py-10 md:py-14 bg-background-50">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="prose-custom">
                {article.sections.map((section, i) => {
                  if (section.type === "h2") {
                    return (
                      <h2
                        key={i}
                        className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4"
                        dangerouslySetInnerHTML={{ __html: section.content }}
                      />
                    );
                  }
                  return (
                    <p
                      key={i}
                      className="text-base text-foreground-700 leading-relaxed mb-4"
                      dangerouslySetInnerHTML={{ __html: section.content }}
                    />
                  );
                })}

                <div className="bg-primary-50 border border-primary-200/60 rounded-xl p-5 md:p-6 my-8">
                  <h3 className="font-heading text-lg font-semibold text-primary-800 mb-3">
                    Podsumowanie
                  </h3>
                  <p className="text-sm md:text-base text-primary-700 leading-relaxed mb-3">
                    <strong>USFansSpreadsheet</strong> to najlepsze narzędzie do porównywania amerykańskich produktów dostępne dla polskich użytkowników. Szeroki zakres 14 kategorii, cotygodniowe aktualizacje i wysoki poziom bezpieczeństwa sprawiają, że jest to must-have dla każdego fana produktów z USA.
                  </p>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:text-primary-800 underline cursor-pointer"
                  >
                    Wróć na stronę główną
                    <i className="ri-arrow-right-line text-sm"></i>
                  </Link>
                </div>

                <p className="text-sm text-foreground-500 mt-8">
                  Ostatnia aktualizacja artykułu: {article.date}. Autor: {article.author}, analityk narzędzi internetowych z 5-letnim doświadczeniem w porównywaniu platform i przewodnikach.
                </p>
              </div>
            </div>
          </article>

          <section className="relative w-full py-12 md:py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-8">
                <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-2">
                  Inne artykuły
                </h2>
                <p className="text-sm text-foreground-600">
                  Sprawdź pozostałe przewodniki i poradniki USFansSpreadsheet
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                {articles
                  .filter((a) => a.slug !== slug)
                  .slice(0, 4)
                  .map((a) => (
                    <Link
                      key={a.slug}
                      to={`/artykul/${a.slug}`}
                      className="group flex flex-col bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-200 cursor-pointer"
                    >
                      <div className="relative w-full aspect-[16/10] overflow-hidden">
                        <img
                          src={a.image}
                          alt={a.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-3 md:p-4">
                        <h4 className="font-heading text-sm font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors leading-snug line-clamp-2">
                          {a.title}
                        </h4>
                        <span className="text-xs text-foreground-500 mt-2 inline-block">
                          {a.readTime}
                        </span>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          </section>

          <section className="relative w-full py-14 md:py-20 bg-background-50">
            <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
              <p className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
                Chcesz zobaczyć <span className="italic text-primary-600">USFansSpreadsheet</span> w akcji?
              </p>
              <p className="text-sm text-foreground-600 mb-6">
                Odwiedź kaspreadsheet.com i odkryj wszystkie kategorie produktów już teraz.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-900 text-background-50 text-sm font-semibold rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-home-4-line text-base"></i>
                  Strona główna
                </Link>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  kaspreadsheet.com
                  <i className="ri-arrow-right-up-line text-base"></i>
                </a>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}